import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { AlertTriangle, Stethoscope, Home, HelpCircle, ShieldPlus, FlaskConical, ShoppingBag, ArrowRight } from 'lucide-react';

const stripTag = (s = '') => String(s || '').replace(/^\s*\[[^\]]*\]\s*/, '').trim();

const URGENCY = {
  RED: { label: 'Go to ER now', bg: '#FEF2F2', text: '#DC2626', border: '#FECACA', icon: AlertTriangle },
  AMBER: { label: 'See a doctor today', bg: '#FFFBEB', text: '#B45309', border: '#FDE68A', icon: Stethoscope },
  YELLOW: { label: 'See a doctor today', bg: '#FFFBEB', text: '#B45309', border: '#FDE68A', icon: Stethoscope },
  BLUE: { label: 'Contact your physician today', bg: '#EFF6FF', text: '#185FA5', border: '#BFDBFE', icon: Stethoscope },
  GREEN: { label: 'Monitor at home', bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0', icon: Home },
};

function pickUrgency(riskFlags) {
  const sev = riskFlags.map((f) => (f.severity || '').toUpperCase());
  if (sev.includes('CRITICAL') || sev.includes('HIGH')) return 'RED';
  if (sev.includes('MODERATE')) return 'YELLOW';
  return 'GREEN';
}

function scrollToHealthMart() {
  const el = document.querySelector('[data-testid="clinical-output"]');
  if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
}

export default function PatientResultCard({ result }) {
  const riskFlags = result.riskFlags || [];
  const soap = result.soap || {};
  const differentials = result.differentials || [];
  const preventive = result.preventiveRecommendations || [];

  const disposition = result.patientDisposition || null;
  const level = disposition ? (disposition.urgency || 'AMBER') : pickUrgency(riskFlags);
  const u = URGENCY[level] || URGENCY.GREEN;
  const Icon = u.icon;

  const topConcern = (riskFlags[0] && riskFlags[0].category)
    || (differentials[0] && differentials[0].condition)
    || stripTag(soap.cc);

  const lineWhat = topConcern
    ? `Your answers point to something a clinician should look at: ${topConcern.toLowerCase()}.`
    : 'No urgent red-flag patterns were found in your answers.';
  const lineUrgent = disposition
    ? (disposition.rationale || 'Please follow the guidance below.')
    : {
        RED: 'This may be time-sensitive — please do not wait.',
        YELLOW: 'This is not an emergency, but you should be seen soon.',
        GREEN: 'There are no signs that need emergency care right now.',
      }[level];
  const lineNext = disposition
    ? disposition.primaryDirective
    : {
        RED: 'Go to the nearest emergency room now, or call your local emergency number.',
        YELLOW: 'Book a visit with a doctor today and bring this summary with you.',
        GREEN: 'You can monitor at home and follow the guidance below. See a doctor if things change.',
      }[level];
  const escalationCriteria = (disposition && Array.isArray(disposition.escalationCriteria))
    ? disposition.escalationCriteria : [];
  const badgeLabel = (disposition && disposition.badge) || u.label;
  const urgentTone = level === 'RED';
  const citations = Array.from(new Set(
    (riskFlags || [])
      .flatMap((f) => Array.isArray(f.evidence_citations)
        ? f.evidence_citations
        : (f.source_guideline ? [f.source_guideline] : []))
      .filter(Boolean)
  ));

  const questions = soap.patientBridge || result.providerFollowup || [];
  const preventiveTags = preventive.slice(0, 3).map((p) => p.topic).filter(Boolean);

  return (
    <div className="mb-6" data-testid="patient-result-card" style={{ fontFamily: "'IBM Plex Sans',sans-serif" }}>
      {/* Urgency badge */}
      <div
        className="rounded-xl border-2 p-4 flex items-center gap-3 mb-4"
        style={{ background: u.bg, borderColor: u.border }}
        data-testid={`urgency-badge-${level.toLowerCase()}`}
      >
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ background: u.text }}>
          <Icon className="w-5 h-5 text-white" />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: u.text }}>What to do</div>
          <div className="text-xl font-bold" style={{ color: u.text, fontFamily: "'Work Sans',sans-serif" }}>{badgeLabel}</div>
        </div>
      </div>

      {/* Three-line plain-language result */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5 mb-4 text-sm" data-testid="patient-plain-result">
        <div className="flex gap-2.5"><HelpCircle className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" /><span className="text-slate-700"><span className="font-semibold text-slate-900">What it might be: </span>{lineWhat}</span></div>
        <div className="flex gap-2.5"><AlertTriangle className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" /><span className="text-slate-700"><span className="font-semibold text-slate-900">How urgent: </span>{lineUrgent}</span></div>
        <div className="flex gap-2.5"><ArrowRight className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" /><span className="text-slate-700"><span className="font-semibold text-slate-900">What to do next: </span>{lineNext}</span></div>
      </div>

      {/* Always-visible escalation safety net — tone scales with acuity to
          avoid over-alarming low-acuity patients (anti-anxiety), while never
          hiding the emergency criteria. */}
      {escalationCriteria.length > 0 && (
        <div
          className="rounded-xl border-2 p-4 mb-4"
          style={urgentTone
            ? { background: '#FEF2F2', borderColor: '#FECACA' }
            : { background: '#FFFBEB', borderColor: '#FDE68A' }}
          data-testid="patient-escalation-banner"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" style={{ color: urgentTone ? '#DC2626' : '#B45309' }} />
            <span className="text-sm font-bold" style={{ color: urgentTone ? '#DC2626' : '#B45309' }}>
              {urgentTone ? 'Seek emergency care immediately if:' : 'Seek emergency care right away if any of these develop:'}
            </span>
          </div>
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700">
            {escalationCriteria.map((c, i) => <li key={i} data-testid={`escalation-item-${i}`}>{c}</li>)}
          </ul>
          {disposition?.basis && (
            <p className="text-[10px] italic text-slate-400 mt-2" data-testid="escalation-basis">Triage basis: {disposition.basis}</p>
          )}
        </div>
      )}

      {/* Three collapsed accordions */}
      <Accordion type="multiple" className="w-full">
        <AccordionItem value="questions" className="bg-white border border-slate-200 rounded-lg mb-3 overflow-hidden">
          <AccordionTrigger data-testid="patient-acc-questions" className="px-5 py-3.5 hover:bg-slate-50 hover:no-underline text-sm font-semibold text-slate-800">
            <span className="flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[#185FA5]" /> Questions to ask your doctor</span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-4 pt-1 border-t border-slate-100">
            {questions.length > 0 ? (
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700">{questions.map((q, i) => <li key={i}>{q}</li>)}</ol>
            ) : <p className="text-sm text-slate-500">No specific questions were generated for this visit.</p>}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="preventive" className="bg-white border border-slate-200 rounded-lg mb-3 overflow-hidden">
          <AccordionTrigger data-testid="patient-acc-preventive" className="px-5 py-3.5 hover:bg-slate-50 hover:no-underline text-sm font-semibold text-slate-800">
            <span className="flex items-center gap-2"><ShieldPlus className="w-4 h-4 text-emerald-600" /> Preventive reminders</span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-4 pt-1 border-t border-slate-100">
            {preventiveTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">{preventiveTags.map((t, i) => (
                <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">{t}</span>
              ))}</div>
            ) : <p className="text-sm text-slate-500">No preventive reminders matched your profile this visit.</p>}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="study" className="bg-white border border-slate-200 rounded-lg mb-3 overflow-hidden">
          <AccordionTrigger data-testid="patient-acc-study" className="px-5 py-3.5 hover:bg-slate-50 hover:no-underline text-sm font-semibold text-slate-800">
            <span className="flex items-center gap-2"><FlaskConical className="w-4 h-4 text-indigo-600" /> You may qualify for a clinical study</span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-4 pt-1 border-t border-slate-100">
            <p className="text-sm text-slate-700">Based on your profile, you may match clinical trials.{' '}
              <button onClick={scrollToHealthMart} className="text-indigo-600 font-medium hover:underline" data-testid="patient-study-healthmart-link">View in Health Mart →</button>
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Guideline-citation footer — reinforces the deterministic,
          evidence-based basis at the moment of the directive. */}
      <div data-testid="patient-guideline-footer" className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-slate-500 leading-snug">
        {citations.length > 0 && (
          <p className="mb-1">
            <span className="font-semibold text-slate-600">Guidance based on:</span> {citations.slice(0, 4).join(' · ')}
          </p>
        )}
        <p><span className="font-semibold text-slate-600">Deterministic, evidence-based decision support — not a diagnosis.</span> This tool helps you understand your answers; it does not replace a clinician.</p>
      </div>

      {/* Health Mart CTA */}
      <button
        onClick={scrollToHealthMart}
        data-testid="patient-health-mart-btn"
        className="w-full mt-2 rounded-xl bg-[#185FA5] hover:bg-[#134c84] text-white font-semibold py-3.5 flex items-center justify-center gap-2 transition-colors shadow-sm"
      >
        <ShoppingBag className="w-4.5 h-4.5" /> Explore Health Mart
      </button>
    </div>
  );
}
